// frontend/src/widgets/WorkflowActionConfigWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-15
// Updated:      2026-10-06 18:45 BST
//
// Description:
//   Advanced workflow automation configuration widget.
//
//   Responsibilities:
//     - List step actions for the selected step
//     - Select active action
//     - Create new action
//     - Update existing action
//     - Reorder actions using sequence_no
//     - Delete actions
//
//   Important:
//     - Controlled by WorkflowPage
//     - This widget only manages action authoring UI
//     - Reordering is done via sequence_no updates, not drag/drop
//
// Mobile Responsiveness (2026-09-10):
//     - Preserves all action selection, create/update/delete, validation and reorder callbacks.
//     - Desktop retains the existing two-pane Action List + Action Editor layout.
//     - Mobile stacks Action List above Action Editor using the same render tree.
//     - Mobile shows one complete action card per internal vertical scroll/snap.
//     - Editor fields collapse from two columns to one on phones.
//     - Retry Policy JSON and Config JSON remain full-width.
//     - New/Up/Down/Save/Delete/Clear actions become touch-safe on mobile.
//     - Dynamic mobile sizing uses the proven Vision pattern:
//         restore previous host sizing;
//         clear temporary list/root sizing;
//         measure the natural first action card;
//         size the action-list viewport to one complete card;
//         wait a second animation frame;
//         resize the first width-matching fixed-height mobile host.
//     - ResizeObserver watches the complete content and first action card so selection,
//       mode or field-layout changes cannot leave the widget clipped.
//     - No duplicate mobile render tree is mounted.
//
// Operational Notes:
//     - No workflow API calls exist in this widget; parent callbacks remain the sole mutation path.
//     - Draft-only edit gating, sequence_no reorder semantics and JSON validation are unchanged.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";
import axiosInstance from "../api/axiosInstance.js";

const ACTION_TYPE_OPTIONS = [
  "send_notification",
  "assign_task",
  "send_email",
  "raise_alert",
  "call_backend_handler",
  "set_job_status",
  "set_task_status",
  "emit_webhook",
  "noop",
];

const ACTION_EVENT_OPTIONS = [
  "on_step_ready",
  "on_step_started",
  "on_step_completed",
  "on_step_failed",
  "on_step_overridden",
];

const FAILURE_MODE_OPTIONS = [
  "fail_step",
  "set_waiting",
  "allow_override",
  "log_only",
];

const emptyForm = {
  sequence_no: "",
  action_key: "",
  display_name: "",
  action_type: "noop",
  on_event: "on_step_ready",
  is_blocking: false,
  failure_mode: "log_only",
  retry_policy_json: "",
  timeout_seconds: "",
  config_json: "",
  is_active: true,
  recipient_types: [],
  channels: ["internal"],
  subject: "",
  body: "",
  resource_id: "",
};

const WorkflowActionConfigWidget = ({
  selectedTemplate = null,
  selectedVersion = null,
  selectedStep = null,
  actions = [],
  selectedActionId = null,
  loading = {
    actions: false,
    savingAction: false,
    deletingAction: false,
    reorderingAction: false,
  },
  onSelectAction,
  onCreateAction,
  onUpdateAction,
  onDeleteAction,
  onMoveActionUp,
  onMoveActionDown,
}) => {
  const { theme } = useTheme();

  const widgetRootRef = useRef(null);
  const contentRef = useRef(null);
  const actionListViewportRef = useRef(null);
  const firstActionCardRef = useRef(null);

  const [mode, setMode] = useState("create");
  const [form, setForm] = useState(emptyForm);
  const [resourceOptions, setResourceOptions] = useState([]);

  useEffect(() => {
    let alive = true;
    axiosInstance.get("resources/").then((res) => {
      if (!alive) return;
      const rows = Array.isArray(res.data) ? res.data : [];
      setResourceOptions(rows.filter((r) => r.active !== false).map((r) => ({ value: String(r.id), label: `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.identifier || `Resource ${r.id}` })));
    }).catch(() => { if (alive) setResourceOptions([]); });
    return () => { alive = false; };
  }, []);

  const selectedAction = useMemo(
    () => actions.find((a) => a.id === selectedActionId) || null,
    [actions, selectedActionId]
  );

  const isDraft = selectedVersion?.status === "draft";
  const canEdit = !!selectedTemplate?.id && !!selectedVersion?.id && !!selectedStep?.id && isDraft;

  useEffect(() => {
    if (selectedAction) {
      setMode("edit");
      setForm({
        sequence_no: selectedAction.sequence_no ?? "",
        action_key: selectedAction.action_key || "",
        display_name: selectedAction.display_name || "",
        action_type: selectedAction.action_type || "noop",
        on_event: selectedAction.on_event || "on_step_ready",
        is_blocking: !!selectedAction.is_blocking,
        failure_mode: selectedAction.failure_mode || "log_only",
        retry_policy_json: selectedAction.retry_policy_json
          ? JSON.stringify(selectedAction.retry_policy_json, null, 2)
          : "",
        timeout_seconds: selectedAction.timeout_seconds ?? "",
        config_json: selectedAction.config_json
          ? JSON.stringify(selectedAction.config_json, null, 2)
          : "",
        recipient_types: selectedAction.config_json?.recipient_types || [],
        channels: selectedAction.config_json?.channels || [selectedAction.config_json?.channel || "internal"],
        subject: selectedAction.config_json?.subject || "",
        body: selectedAction.config_json?.body || "",
        resource_id: selectedAction.config_json?.resource_id ? String(selectedAction.config_json.resource_id) : "",
        is_active: typeof selectedAction.is_active === "boolean" ? selectedAction.is_active : true,
      });
    } else {
      setMode("create");
      setForm(emptyForm);
    }
  }, [selectedAction]);

  const actionBtn = {
    background: theme.button_action?.normal || "#000",
    color: "#fff",
    padding: "8px 12px",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
  };

  const generalBtn = {
    background: theme.button_general?.normal || "#222",
    color: "#fff",
    padding: "8px 12px",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
  };

  const dangerBtn = {
    background: "#8c1f1f",
    color: "#fff",
    padding: "8px 12px",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
  };

  const inputStyle = {
    width: "100%",
    minHeight: 44,
    padding: "8px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    boxSizing: "border-box",
    fontSize: 14,
    backgroundColor: "#fff",
  };

  const labelStyle = {
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
    marginBottom: 4,
  };

  const mutedStyle = {
    fontSize: 12,
    color: "#6b7280",
  };

  const parseOptionalInt = (value) => {
    if (value === "" || value === null || typeof value === "undefined") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const parseOptionalJson = (value, fieldLabel) => {
    if (!String(value || "").trim()) return null;
    try {
      return JSON.parse(value);
    } catch {
      throw new Error(`${fieldLabel} must contain valid JSON.`);
    }
  };

  const buildPayload = () => {
    if (!form.action_key.trim()) {
      throw new Error("Action key is required.");
    }
    if (!form.display_name.trim()) {
      throw new Error("Display name is required.");
    }

    return {
      sequence_no: parseOptionalInt(form.sequence_no),
      action_key: form.action_key.trim(),
      display_name: form.display_name.trim(),
      action_type: form.action_type,
      on_event: form.on_event,
      is_blocking: !!form.is_blocking,
      failure_mode: form.failure_mode,
      retry_policy_json: parseOptionalJson(form.retry_policy_json, "Retry policy JSON"),
      timeout_seconds: parseOptionalInt(form.timeout_seconds),
      config_json: form.action_type === "send_notification"
        ? { recipient_types: form.recipient_types, channels: form.channels, subject: form.subject.trim(), body: form.body.trim() }
        : form.action_type === "assign_task"
          ? { resource_id: parseOptionalInt(form.resource_id), assignment_role: "primary" }
          : parseOptionalJson(form.config_json, "Config JSON"),
      is_active: !!form.is_active,
    };
  };

  const handleSubmit = async () => {
    if (!canEdit) {
      alert("Only draft versions with a selected step can be edited.");
      return;
    }

    try {
      const payload = buildPayload();

      if (mode === "edit" && selectedAction) {
        await onUpdateAction?.(selectedAction.id, payload);
      } else {
        await onCreateAction?.(payload);
        setForm(emptyForm);
      }
    } catch (err) {
      alert(err?.message || "Failed to save action.");
    }
  };

  const handleDelete = async () => {
    if (!selectedAction) {
      alert("Select an action first.");
      return;
    }
    if (!window.confirm(`Delete action "${selectedAction.display_name}"?`)) {
      return;
    }
    await onDeleteAction?.(selectedAction.id);
  };

  const handleNewAction = () => {
    onSelectAction?.(null);
    setMode("create");
    setForm(emptyForm);
  };

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const root = widgetRootRef.current;
    const content = contentRef.current;
    const viewport = actionListViewportRef.current;
    const firstCard = firstActionCardRef.current;

    if (!root || !content || !viewport) return undefined;

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;
      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];
      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();
        const widthMatches = Math.abs(rect.width - rootRect.width) < 90;
        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";

      if (!mediaQuery.matches) return;

      animationFrame = requestAnimationFrame(() => {
        const currentFirstCard = firstActionCardRef.current;

        if (currentFirstCard) {
          const cardHeight = Math.ceil(
            Math.max(
              currentFirstCard.getBoundingClientRect().height || 0,
              currentFirstCard.scrollHeight || 0
            )
          );

          if (Number.isFinite(cardHeight) && cardHeight > 0) {
            const viewportHeight = cardHeight + 16;
            viewport.style.height = `${viewportHeight}px`;
            viewport.style.minHeight = `${viewportHeight}px`;
          }
        }

        root.style.height = "auto";
        root.style.minHeight = "0px";
        content.style.height = "auto";
        content.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();
          if (!host) return;

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();
          const wrapperChromeHeight = Math.max(0, rootRect.top - hostRect.top);

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (!Number.isFinite(requiredHostHeight) || requiredHostHeight <= 0) return;

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    resizeObserver?.observe(content);
    if (resizeObserver && firstCard) {
      resizeObserver.observe(firstCard);
    }

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";
    };
  }, [
    actions.length,
    canEdit,
    loading.actions,
    loading.deletingAction,
    loading.reorderingAction,
    loading.savingAction,
    mode,
    selectedActionId,
    selectedStep?.id,
  ]);

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
    >
      <style>{`
        @media (max-width: 767px) {
          .vx-workflow-action-mobile-button {
            width: 100% !important;
            min-height: 44px;
          }

          .vx-workflow-action-list-viewport {
            overflow-y: auto !important;
            overflow-x: hidden !important;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            scroll-snap-type: y mandatory;
          }

          .vx-workflow-action-list-viewport::-webkit-scrollbar {
            display: none;
          }

          .vx-workflow-action-card {
            min-width: 0;
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }

          .vx-workflow-action-editor input,
          .vx-workflow-action-editor select,
          .vx-workflow-action-editor textarea {
            min-height: 44px;
          }
        }
      `}</style>

      <div
        ref={contentRef}
        className="flex h-auto min-h-0 flex-col gap-3.5 md:h-full"
      >
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div>
          <div style={{ fontWeight: 700 }}>Advanced Automation</div>
          <div style={mutedStyle}>
            {!selectedStep
              ? "Select a step first."
              : loading.actions
                ? "Loading actions..."
                : `${actions.length} action(s)`}
          </div>
        </div>

        <div className="w-full sm:w-auto">
          <button
            type="button"
            className="vx-workflow-action-mobile-button"
            onClick={handleNewAction}
            disabled={!canEdit}
            style={{
              ...generalBtn,
              opacity: canEdit ? 1 : 0.7,
              cursor: canEdit ? "pointer" : "not-allowed",
            }}
          >
            New Action
          </button>
        </div>
      </div>

      <div className="grid h-auto min-h-0 grid-cols-1 gap-4 md:h-full md:flex-1 lg:grid-cols-[1fr_1.25fr]">
        <div className="flex h-auto min-h-0 flex-col md:h-full">
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            {selectedStep ? `Automation for: ${selectedStep.title}` : "Automation List"}
          </div>

          <div ref={actionListViewportRef} className="vx-workflow-action-list-viewport flex h-auto min-h-0 flex-none flex-col gap-2 overflow-visible md:flex-1 md:overflow-y-auto">
            {actions.map((action, index) => {
              const selected = action.id === selectedActionId;

              return (
                <div
                  key={action.id}
                  ref={index === 0 ? firstActionCardRef : null}
                  className="vx-workflow-action-card"
                  style={{
                    border: selected ? `2px solid ${theme.accent_color || "#ff7a00"}` : "1px solid #e5e7eb",
                    borderRadius: 10,
                    background: selected ? "#fff7ed" : "#fff",
                    padding: 10,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onSelectAction?.(action.id)}
                    style={{
                      width: "100%",
                      minHeight: 44,
                      textAlign: "left",
                      background: "transparent",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>
                      {action.sequence_no}. {action.display_name}
                    </div>
                    <div style={mutedStyle}>{action.action_key}</div>
                    <div style={{ ...mutedStyle, marginTop: 6 }}>
                      {action.action_type} · {action.on_event} · {action.failure_mode}
                      {!action.is_active ? " · inactive" : ""}
                    </div>
                  </button>

                  <div className="mt-2.5 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                    <button
                      type="button"
                      className="vx-workflow-action-mobile-button"
                      onClick={() => onMoveActionUp?.(action, index)}
                      disabled={!canEdit || index === 0 || loading.reorderingAction}
                      style={{
                        ...generalBtn,
                        padding: "6px 10px",
                        opacity: !canEdit || index === 0 || loading.reorderingAction ? 0.7 : 1,
                        cursor: !canEdit || index === 0 || loading.reorderingAction ? "not-allowed" : "pointer",
                      }}
                    >
                      Up
                    </button>

                    <button
                      type="button"
                      className="vx-workflow-action-mobile-button"
                      onClick={() => onMoveActionDown?.(action, index)}
                      disabled={!canEdit || index === actions.length - 1 || loading.reorderingAction}
                      style={{
                        ...generalBtn,
                        padding: "6px 10px",
                        opacity:
                          !canEdit || index === actions.length - 1 || loading.reorderingAction ? 0.7 : 1,
                        cursor:
                          !canEdit || index === actions.length - 1 || loading.reorderingAction
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      Down
                    </button>
                  </div>
                </div>
              );
            })}

            {!loading.actions && selectedStep && actions.length === 0 && (
              <div style={mutedStyle}>No automation configured. Manual tasks do not require automation.</div>
            )}

            {!selectedStep && (
              <div style={mutedStyle}>Select a step to load actions.</div>
            )}
          </div>
        </div>

        <div className="vx-workflow-action-editor flex h-auto min-h-0 flex-col md:h-full">
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            {mode === "edit" && selectedAction ? "Edit Action" : "Create Action"}
          </div>

          <div className="h-auto min-h-0 overflow-visible pr-0 md:flex-1 md:overflow-y-auto md:pr-1">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <div style={labelStyle}>Sequence No</div>
                <input
                  type="number"
                  min="1"
                  value={form.sequence_no}
                  onChange={(e) => setForm((p) => ({ ...p, sequence_no: e.target.value }))}
                  style={inputStyle}
                  disabled={!canEdit}
                />
              </div>

              <div>
                <div style={labelStyle}>Timeout Seconds</div>
                <input
                  type="number"
                  min="1"
                  value={form.timeout_seconds}
                  onChange={(e) => setForm((p) => ({ ...p, timeout_seconds: e.target.value }))}
                  style={inputStyle}
                  disabled={!canEdit}
                />
              </div>

              <div>
                <div style={labelStyle}>Action Key</div>
                <input
                  type="text"
                  value={form.action_key}
                  onChange={(e) => setForm((p) => ({ ...p, action_key: e.target.value }))}
                  style={inputStyle}
                  disabled={!canEdit}
                />
              </div>

              <div>
                <div style={labelStyle}>Display Name</div>
                <input
                  type="text"
                  value={form.display_name}
                  onChange={(e) => setForm((p) => ({ ...p, display_name: e.target.value }))}
                  style={inputStyle}
                  disabled={!canEdit}
                />
              </div>

              <div>
                <div style={labelStyle}>Action Type</div>
                <select
                  value={form.action_type}
                  onChange={(e) => setForm((p) => ({ ...p, action_type: e.target.value }))}
                  style={inputStyle}
                  disabled={!canEdit}
                >
                  {ACTION_TYPE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div style={labelStyle}>On Event</div>
                <select
                  value={form.on_event}
                  onChange={(e) => setForm((p) => ({ ...p, on_event: e.target.value }))}
                  style={inputStyle}
                  disabled={!canEdit}
                >
                  {ACTION_EVENT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              {form.action_type === "send_notification" && (
                <div className="md:col-span-2" style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, background: "#f8fafc" }}>
                  <div style={{ ...labelStyle, marginBottom: 8 }}>Recipients</div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {[['job_owner','Job Owner'],['job_watchers','Job Watchers'],['task_assignee','Task Assignee'],['actor','Person who triggered it']].map(([value,label]) => (
                      <label key={value} className="flex items-center gap-2 text-[13px]"><input type="checkbox" checked={form.recipient_types.includes(value)} disabled={!canEdit} onChange={(e)=>setForm((p)=>({...p,recipient_types:e.target.checked?[...new Set([...p.recipient_types,value])]:p.recipient_types.filter((x)=>x!==value)}))}/>{label}</label>
                    ))}
                  </div>
                  <div style={{ ...labelStyle, marginTop: 12, marginBottom: 8 }}>Channels</div>
                  <div className="flex flex-wrap gap-4">
                    {[['internal','Internal Alert'],['pwa_push','PWA Push'],['email','Email']].map(([value,label]) => (
                      <label key={value} className="flex items-center gap-2 text-[13px]"><input type="checkbox" checked={form.channels.includes(value)} disabled={!canEdit} onChange={(e)=>setForm((p)=>({...p,channels:e.target.checked?[...new Set([...p.channels,value])]:p.channels.filter((x)=>x!==value)}))}/>{label}</label>
                    ))}
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-3">
                    <div><div style={labelStyle}>Title</div><input value={form.subject} onChange={(e)=>setForm((p)=>({...p,subject:e.target.value}))} style={inputStyle} disabled={!canEdit}/></div>
                    <div><div style={labelStyle}>Message</div><textarea value={form.body} onChange={(e)=>setForm((p)=>({...p,body:e.target.value}))} style={{...inputStyle,minHeight:90,resize:'vertical'}} disabled={!canEdit}/></div>
                  </div>
                </div>
              )}

              {form.action_type === "assign_task" && (
                <div className="md:col-span-2" style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, background: "#f8fafc" }}>
                  <div style={labelStyle}>Assign This Task To</div>
                  <select value={form.resource_id} onChange={(e)=>setForm((p)=>({...p,resource_id:e.target.value}))} style={inputStyle} disabled={!canEdit}>
                    <option value="">Select resource…</option>{resourceOptions.map((r)=><option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              )}

              <div>
                <div style={labelStyle}>Failure Mode</div>
                <select
                  value={form.failure_mode}
                  onChange={(e) => setForm((p) => ({ ...p, failure_mode: e.target.value }))}
                  style={inputStyle}
                  disabled={!canEdit}
                >
                  {FAILURE_MODE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex min-h-[44px] flex-col justify-end gap-2">
                <label className="flex min-h-[44px] items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={!!form.is_blocking}
                    onChange={(e) => setForm((p) => ({ ...p, is_blocking: e.target.checked }))}
                    disabled={!canEdit}
                  />
                  Blocking
                </label>

                <label className="flex min-h-[44px] items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={!!form.is_active}
                    onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                    disabled={!canEdit}
                  />
                  Active
                </label>
              </div>

              <div className="md:col-span-2">
                <div style={labelStyle}>Retry Policy JSON</div>
                <textarea
                  value={form.retry_policy_json}
                  onChange={(e) => setForm((p) => ({ ...p, retry_policy_json: e.target.value }))}
                  style={{ ...inputStyle, minHeight: 100, resize: "vertical", fontFamily: "monospace" }}
                  disabled={!canEdit}
                  placeholder='e.g. {"max_attempts":3,"delay_seconds":30}'
                />
              </div>

              {!['send_notification','assign_task'].includes(form.action_type) && (<div className="md:col-span-2">
                <div style={labelStyle}>Config JSON</div>
                <textarea
                  value={form.config_json}
                  onChange={(e) => setForm((p) => ({ ...p, config_json: e.target.value }))}
                  style={{ ...inputStyle, minHeight: 120, resize: "vertical", fontFamily: "monospace" }}
                  disabled={!canEdit}
                  placeholder='e.g. {"handler":"notify_ops"}'
                />
              </div>)}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:gap-2.5">
              <button
                type="button"
                className="vx-workflow-action-mobile-button"
                onClick={handleSubmit}
                disabled={!canEdit || loading.savingAction}
                style={{
                  ...actionBtn,
                  opacity: !canEdit || loading.savingAction ? 0.7 : 1,
                  cursor: !canEdit || loading.savingAction ? "not-allowed" : "pointer",
                }}
              >
                {loading.savingAction
                  ? "Saving..."
                  : mode === "edit" && selectedAction
                    ? "Update Action"
                    : "Create Action"}
              </button>

              {mode === "edit" && selectedAction && (
                <button
                  type="button"
                  className="vx-workflow-action-mobile-button"
                  onClick={handleDelete}
                  disabled={!canEdit || loading.deletingAction}
                  style={{
                    ...dangerBtn,
                    opacity: !canEdit || loading.deletingAction ? 0.7 : 1,
                    cursor: !canEdit || loading.deletingAction ? "not-allowed" : "pointer",
                  }}
                >
                  {loading.deletingAction ? "Deleting..." : "Delete Action"}
                </button>
              )}

              <button
                type="button"
                className="vx-workflow-action-mobile-button"
                onClick={handleNewAction}
                style={generalBtn}
              >
                Clear / New
              </button>
            </div>

            {!canEdit && (
              <div style={{ ...mutedStyle, marginTop: 12 }}>
                Only draft versions with a selected step can be edited.
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default WorkflowActionConfigWidget;